import algoliasearch from 'algoliasearch/lite'
import Head from 'next/head'

import { fetchJSON, isBlank, isPresent } from 'utils'

import collectionToCollectionCardProps from 'converters/collectionToCollectionCardProps'
import prismicArticleToArticleCardProps from 'converters/prismicArticleToArticleCardProps'
import prismicArtisanToArtisanCardProps from 'converters/prismicArtisanToArtisanCardProps'

import ArticleCard from 'components/ArticleCard'
import ArtisanCard from 'components/ArtisanCard'
import Collection from 'components/Collection'
import Divider from 'components/Divider'
import Header from 'components/Header'
import Link from 'components/Link'
import Spacer from 'components/Spacer'

import { CollectionLink, CollectionsColumns, Columns, ResultSet, Results, Root } from './search.style'

export default function Search({ query, results, productCollection, productFacets }) {
  const { products, artisans, articles, collections } = results

  if (isBlank(query))
    return (
      <>
        <Head>
          <title>No results</title>
        </Head>
        <Root>
          <Header size="xl">No results</Header>
        </Root>
      </>
    )

  return (
    <>
      <Head>
        <title>Search results for ‘{query}’</title>
      </Head>
      <Root>
        <Header size="xl">Search results for ‘{query}’</Header>
        <Spacer y={8} />
        <Results>
          {isPresent(products) && (
            <ResultSet>
              <Header>Products</Header>
              <Spacer y={4} />
              <Divider />
              <Spacer y={6} />
              <Collection collection={productCollection} facets={productFacets} headerDisabled />
            </ResultSet>
          )}
          {isPresent(artisans) && (
            <ResultSet>
              <Header>Artisans</Header>
              <Spacer y={4} />
              <Divider />
              <Spacer y={6} />
              <Columns>{artisans.map(ArtisanCard)}</Columns>
            </ResultSet>
          )}
          {isPresent(articles) && (
            <ResultSet>
              <Header>Journal</Header>
              <Spacer y={4} />
              <Divider />
              <Spacer y={6} />
              <Columns>{articles.map(ArticleCard)}</Columns>
            </ResultSet>
          )}
          {isPresent(collections) && (
            <ResultSet>
              <Header>Collections</Header>
              <Spacer y={4} />
              <Divider />
              <Spacer y={6} />
              <CollectionsColumns>
                {collections.map(({ handle, title }) => (
                  <CollectionLink key={handle}>
                    <Link href={`journal/${handle}`} animated>
                      {title}
                    </Link>
                  </CollectionLink>
                ))}
              </CollectionsColumns>
            </ResultSet>
          )}
        </Results>
      </Root>
    </>
  )
}

const queryAlgolia = async (query) => {
  const algolia = algoliasearch('DIVOOX7Z4L', 'ead999a12664cc933529dd4f786b17b4')
  const limit = 300
  const results = {}

  if (isBlank(query)) return results

  try {
    const queries = [
      {
        indexName: 'tav3_products',
        query,
        params: {
          hitsPerPage: limit,
        },
      },
      {
        indexName: 'tav3_artisans',
        query,
        params: {
          hitsPerPage: limit,
        },
      },
      {
        indexName: 'tav3_collections',
        query,
        params: {
          hitsPerPage: limit,
        },
      },
      {
        indexName: 'tav3_articles',
        query,
        params: {
          hitsPerPage: limit,
        },
      },
    ]
    const response = await algolia.search(queries)

    for (const result of response.results) {
      results[result.index.replace('tav3_', '')] = result.hits
    }
  } catch (e) {
    console.error(e)
  } // eslint-disable-line no-empty

  return results
}

export async function getServerSideProps({ query }) {
  let productCollection = null
  let productFacets = null

  const results = await queryAlgolia(query.q)

  if (isPresent(results.products)) {
    const variant_ids = results.products.map(({ variant_id }) => variant_id)

    productCollection = {
      products: results.products,
    }

    const response = await await fetchJSON(`${process.env.API_URI}/search/facets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ variant_ids }),
    })

    productFacets = response.facets
  }

  // if (isPresent(results.products)) results.products = results.products.map(collectionProductToProductCardProps)
  if (isPresent(results.artisans)) results.artisans = results.artisans.map(prismicArtisanToArtisanCardProps)
  if (isPresent(results.collections)) results.collections = results.collections.map(collectionToCollectionCardProps)
  if (isPresent(results.articles)) results.articles = results.articles.map(prismicArticleToArticleCardProps)

  return {
    props: {
      query: query.q || null,
      results,
      productCollection,
      productFacets,
    },
  }
}
